import { makeAutoObservable, reaction, toJS } from "mobx";
import RootStore from "stores/RootStore";
import { utils } from "@intrasync/utils";
import ServiceStore from "stores/ServiceStore";
import { getDateRange, getLastWorkday, getNextWorkday } from "@intrasync/utils";
import DepartmentProductionScheduleRightHeaderCell from "./DepartmentProductionScheduleCells/DepartmentProductionSchedule.RightHeaderCell";
import DepartmentProductionScheduleRightBodyCell from "./DepartmentProductionScheduleCells/DepartmentProductionSchedule.RightBodyCell";
import DepartmentProductionScheduleLeftHeaderCell from "./DepartmentProductionScheduleCells/DepartmentProductionSchedule.LeftHeaderCell";
import DepartmentProductionScheduleLeftBodyCell from "./DepartmentProductionScheduleCells/DepartmentProductionSchedule.LeftBodyCell";
import type { productionSchedule } from "types/api-types/scheduling/production-schedules/production-schedules.types";
import type { workOrder } from "types/api-types/manufacturing/work-orders/work-orders.types";
import type { product } from "types/api-types/manufacturing/products/products.types";
import type { form } from "types/api-types/manufacturing/forms/forms.types";
import type { productionDepartment } from "types/api-types/scheduling/production-departments/production-departments.types";
import type { salesOrderLine } from "types/api-types/scheduling/sales-order-lines/sales-order-lines.types";
import type { salesOrder } from "types/api-types/scheduling/sales-orders/sales-orders.types";

// Enriched schedule entity with joined data for display
export interface EnrichedScheduleEntity {
  // From ProductionSchedule or WorkOrder
  id: string;
  scheduleDate: string;
  productId: string;
  formId: string;
  salesOrderLineId?: string;
  quantity: number;
  status: productionSchedule["status"] | workOrder["status"];
  // Source type to distinguish between production schedules and work orders
  sourceType: "production_schedule" | "work_order";
  // Enriched from Product
  productName: string;
  productCode: string;
  // Enriched from Form
  formName: string;
  // Enriched from Department
  departmentId: string;
  departmentName: string;
  // Enriched from Sales Order Line
  lineNumber?: number;
  linePriority?: number;
  anticipationDate?: string;
  lineStatus?: salesOrderLine["status"];
  // Enriched from Sales Order
  salesOrderId?: string;
  orderNumber?: string;
  jobName?: string;
  customerId?: string;
  requiredDate?: string;
  // Work Order specific fields
  workOrderNumber?: string;
  pieceNumber?: number;
  priority?: number;
}

export type ViewMode = "byDepartment" | "byForm";

class DepartmentProductionScheduleStore {
  rootStore: RootStore;

  // View mode: toggle between department and form columns
  viewMode: ViewMode = "byDepartment";

  // Selection state
  currentlySelectedEntities: string[] = [];

  // Drag-and-drop state
  draggedEntityId: string | null = null;
  dropTargetDate: string | null = null;
  dropZoneRegistry: Map<string, DOMRect> = new Map();

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      dropZoneRegistry: false,
    });
    this.rootStore = rootStore;

    // Recompute schedule grid when entities change
    reaction(
      () => this.entities.map((entity) => ({ ...entity })),
      () => {
        if (!this.departmentProductionScheduleActive) return null;
        if (this.rootStore.scheduleGridStore?.namespaces?.DepartmentProductionSchedule) {
          this.rootStore.scheduleGridStore?.namespaces?.DepartmentProductionSchedule.recomputeScheduler();
        }
      }
    );

    // Recompute when view mode changes
    reaction(
      () => this.viewMode,
      () => {
        if (!this.departmentProductionScheduleActive) return null;
        if (this.rootStore.scheduleGridStore?.namespaces?.DepartmentProductionSchedule) {
          this.rootStore.scheduleGridStore?.namespaces?.DepartmentProductionSchedule.recomputeScheduler();
        }
      }
    );
  }

  // ========== View Mode Actions ==========

  setViewMode = (mode: ViewMode) => {
    this.viewMode = mode;
  };

  toggleViewMode = () => {
    this.viewMode = this.viewMode === "byDepartment" ? "byForm" : "byDepartment";
  };

  // ========== Selection Actions ==========

  setCurrentlySelectedEntities = (entityId: string) => {
    const foundIndex = this.currentlySelectedEntities.findIndex(
      (currentEntityId) => currentEntityId === entityId
    );

    if (foundIndex !== -1) {
      this.currentlySelectedEntities = this.currentlySelectedEntities.filter(
        (currentEntityId) => currentEntityId !== entityId
      );
      return;
    }

    this.currentlySelectedEntities = [...this.currentlySelectedEntities, entityId];
  };

  clearCurrentlySelectedEntities = () => {
    this.currentlySelectedEntities = [];
  };

  // ========== Move Actions (Button-based) ==========

  moveCurrentlySelectedEntities = async (direction: "up" | "down") => {
    const productionSchedulesStore = this.rootStore.productionSchedulesStore as ServiceStore;
    const workOrdersStore = this.rootStore.workOrdersStore as ServiceStore;

    const currentlySelectedEntities: EnrichedScheduleEntity[] = this.entities.filter(
      (entity: EnrichedScheduleEntity) => this.currentlySelectedEntities.includes(entity.id)
    );

    if (currentlySelectedEntities.length === 0) {
      return;
    }

    // Separate by source type
    const productionEntities = currentlySelectedEntities.filter(
      (e) => e.sourceType === "production_schedule"
    );
    const workOrderEntities = currentlySelectedEntities.filter(
      (e) => e.sourceType === "work_order"
    );

    try {
      const updatePromises: Promise<unknown>[] = [];

      // Handle production schedules
      if (productionEntities.length > 0 && productionSchedulesStore) {
        const groupedByDate = utils.groupBy(productionEntities, "scheduleDate");
        const sortedDates = Object.keys(groupedByDate).sort();

        sortedDates.forEach((date) => {
          const entities = groupedByDate[date];
          const newDate = direction === "up" ? getLastWorkday(date) : getNextWorkday(date);
          const includedIds = entities.map((entity) => entity.id);

          updatePromises.push(
            productionSchedulesStore.patch(
              null,
              { scheduleDate: newDate },
              { query: { id: { $in: includedIds } } }
            )
          );
        });
      }

      // Handle work orders
      if (workOrderEntities.length > 0 && workOrdersStore) {
        const groupedByDate = utils.groupBy(workOrderEntities, "scheduleDate");
        const sortedDates = Object.keys(groupedByDate).sort();

        sortedDates.forEach((date) => {
          const entities = groupedByDate[date];
          const newDate = direction === "up" ? getLastWorkday(date) : getNextWorkday(date);
          const includedIds = entities.map((entity) => entity.id);

          updatePromises.push(
            workOrdersStore.patch(
              null,
              { scheduledDate: newDate },
              { query: { id: { $in: includedIds } } }
            )
          );
        });
      }

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error moving scheduled items:", error);
    }
  };

  // ========== Drag-and-Drop Actions ==========

  setDragState = (entityId: string | null, targetDate: string | null) => {
    this.draggedEntityId = entityId;
    this.dropTargetDate = targetDate;
  };

  clearDragState = () => {
    this.draggedEntityId = null;
    this.dropTargetDate = null;
  };

  registerDropZone = (date: string, rect: DOMRect) => {
    this.dropZoneRegistry.set(date, rect);
  };

  unregisterDropZone = (date: string) => {
    this.dropZoneRegistry.delete(date);
  };

  findDropTarget = (clientX: number, clientY: number): string | null => {
    for (const [date, rect] of this.dropZoneRegistry) {
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return date;
      }
    }
    return null;
  };

  handleDrop = (entityId: string, targetDate: string) => {
    const entity = this.entities.find((e) => e.id === entityId);
    if (!entity || entity.scheduleDate === targetDate) {
      this.clearDragState();
      return;
    }

    // Use appropriate store based on source type
    if (entity.sourceType === "work_order") {
      const workOrdersStore = this.rootStore.workOrdersStore as ServiceStore;
      if (workOrdersStore) {
        workOrdersStore.patch(entityId, { scheduledDate: targetDate });
      }
    } else {
      const productionSchedulesStore = this.rootStore.productionSchedulesStore as ServiceStore;
      if (productionSchedulesStore) {
        productionSchedulesStore.patch(entityId, { scheduleDate: targetDate });
      }
    }
    this.clearDragState();
  };

  handleMultiDrop = (targetDate: string) => {
    if (this.currentlySelectedEntities.length === 0) {
      this.clearDragState();
      return;
    }

    const productionSchedulesStore = this.rootStore.productionSchedulesStore as ServiceStore;
    const workOrdersStore = this.rootStore.workOrdersStore as ServiceStore;

    // Separate selected entities by source type
    const selectedEntities = this.entities.filter((e) =>
      this.currentlySelectedEntities.includes(e.id)
    );
    const productionIds = selectedEntities
      .filter((e) => e.sourceType === "production_schedule")
      .map((e) => e.id);
    const workOrderIds = selectedEntities
      .filter((e) => e.sourceType === "work_order")
      .map((e) => e.id);

    // Update production schedules
    if (productionIds.length > 0 && productionSchedulesStore) {
      productionSchedulesStore.patch(
        null,
        { scheduleDate: targetDate },
        { query: { id: { $in: productionIds } } }
      );
    }

    // Update work orders
    if (workOrderIds.length > 0 && workOrdersStore) {
      workOrdersStore.patch(
        null,
        { scheduledDate: targetDate },
        { query: { id: { $in: workOrderIds } } }
      );
    }

    this.clearCurrentlySelectedEntities();
    this.clearDragState();
  };

  // ========== Row Height Calculation ==========

  getRowHeight = (index: number): number => {
    // Header row (index 0)
    if (index === 0) {
      return 40;
    }

    const modifiedIndex = index - 1; // Subtract 1 to account for the header row
    if (this.dateRange.length === 0) {
      return 50;
    }

    const date = this.dateRange[modifiedIndex];
    if (!date) {
      return 50;
    }

    const dateEntities = this.entities.filter((entity) => entity.scheduleDate === date);

    // Count items per column based on view mode
    const groupKey = this.viewMode === "byDepartment" ? "departmentId" : "formId";
    const columnCounts: Record<string, number> = {};

    dateEntities.forEach((entity) => {
      const key = entity[groupKey] as string;
      if (key) {
        columnCounts[key] = (columnCounts[key] || 0) + 1;
      }
    });

    const maxCount = Math.max(0, ...Object.values(columnCounts));

    // Calculate height based on item count
    // Cell: 52px height + 4px margin = 56px per item
    // Container: 4px padding + 3px border adjustment = 7px overhead
    const ITEM_HEIGHT = 56;
    const MIN_HEIGHT = 70;
    const CONTAINER_OVERHEAD = 12;

    if (maxCount <= 0) return MIN_HEIGHT;

    // Always calculate based on actual count plus container overhead
    return Math.max(MIN_HEIGHT, maxCount * ITEM_HEIGHT + CONTAINER_OVERHEAD);
  };

  // ========== Computed Properties ==========

  get currentDepartments() {
    const departmentStore = this.rootStore.productionDepartmentsStore as ServiceStore;
    if (!departmentStore || !departmentStore.entities) return [];
    const departments = departmentStore.entities as productionDepartment[];

    // Define priority order for departments (lower = first)
    const priorityOrder: Record<string, number> = {
      'Small Wet': 1,
      'Large Wet': 2,
      'Drycast': 3,
    };

    return [...departments].sort((a, b) => {
      const priorityA = priorityOrder[a.departmentName] ?? 999;
      const priorityB = priorityOrder[b.departmentName] ?? 999;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // Fall back to alphabetical for remaining departments
      return a.departmentName.localeCompare(b.departmentName);
    });
  }

  get currentForms() {
    const formsStore = this.rootStore.formsStore as ServiceStore;
    if (!formsStore || !formsStore.entities) return [];
    return formsStore.entities as form[];
  }

  get columnHeaders() {
    if (this.viewMode === "byDepartment") {
      return this.departmentColumnHeaders;
    }
    return this.formColumnHeaders;
  }

  get departmentColumnHeaders() {
    const departments = this.currentDepartments;

    const departmentColumns = departments.map((dept: productionDepartment) => ({
      width: 400,
      title: dept.departmentName || `Dept ${dept.id}`,
      key: dept.id,
      headerKey: "departmentId",
      departmentId: dept.id,
      component: DepartmentProductionScheduleRightHeaderCell,
      bodyComponent: DepartmentProductionScheduleRightBodyCell,
    }));

    return [
      {
        width: 130,
        title: "Date",
        key: "scheduleDate",
        component: DepartmentProductionScheduleLeftHeaderCell,
        bodyComponent: DepartmentProductionScheduleLeftBodyCell,
      },
      ...departmentColumns,
    ];
  }

  get formColumnHeaders() {
    const forms = this.currentForms;

    const formColumns = forms.map((frm: form) => ({
      width: 400,
      title: frm.formName || `Form ${frm.id}`,
      key: frm.id,
      headerKey: "formId",
      formId: frm.id,
      component: DepartmentProductionScheduleRightHeaderCell,
      bodyComponent: DepartmentProductionScheduleRightBodyCell,
    }));

    return [
      {
        width: 130,
        title: "Date",
        key: "scheduleDate",
        component: DepartmentProductionScheduleLeftHeaderCell,
        bodyComponent: DepartmentProductionScheduleLeftBodyCell,
      },
      ...formColumns,
    ];
  }

  get entities(): EnrichedScheduleEntity[] {
    const productionSchedulesStore = this.rootStore.productionSchedulesStore as ServiceStore;
    const workOrdersStore = this.rootStore.workOrdersStore as ServiceStore;
    const productsStore = this.rootStore.productsStore as ServiceStore;
    const formsStore = this.rootStore.formsStore as ServiceStore;
    const productionDepartmentsStore = this.rootStore.productionDepartmentsStore as ServiceStore;
    const salesOrderLinesStore = this.rootStore.salesOrderLinesStore as ServiceStore;
    const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;

    // Build lookup maps for O(1) access
    const productsMap = new Map<string, product>(
      (productsStore?.entities as product[])?.map((p) => [p.id, p]) || []
    );
    const formsMap = new Map<string, form>(
      (formsStore?.entities as form[])?.map((f) => [f.id, f]) || []
    );
    const departmentsMap = new Map<string, productionDepartment>(
      (productionDepartmentsStore?.entities as productionDepartment[])?.map((d) => [d.id, d]) || []
    );
    const salesOrderLinesMap = new Map<string, salesOrderLine>(
      (salesOrderLinesStore?.entities as salesOrderLine[])?.map((sol) => [sol.id, sol]) || []
    );
    const salesOrdersMap = new Map<string, salesOrder>(
      (salesOrdersStore?.entities as salesOrder[])?.map((so) => [so.id, so]) || []
    );

    // Build enriched production schedules
    const productionEntities: EnrichedScheduleEntity[] = (
      (productionSchedulesStore?.entities as productionSchedule[]) || []
    )
      .filter((schedule) => schedule.status !== "cancelled")
      .map((schedule) => {
        const prod = productsMap.get(schedule.productId);
        const frm = formsMap.get(schedule.formId);
        // Use schedule's departmentId if available, otherwise fall back to product's departmentId
        const deptId = schedule.departmentId || prod?.departmentId || "";
        const dept = departmentsMap.get(deptId);

        // Get order line and sales order for traceability
        const orderLine = schedule.salesOrderLineId
          ? salesOrderLinesMap.get(schedule.salesOrderLineId)
          : undefined;
        const salesOrderData = orderLine?.salesOrderId
          ? salesOrdersMap.get(orderLine.salesOrderId)
          : undefined;

        return {
          // From ProductionSchedule
          id: schedule.id,
          scheduleDate: schedule.scheduleDate.split("T")[0],
          productId: schedule.productId,
          formId: schedule.formId,
          salesOrderLineId: schedule.salesOrderLineId,
          quantity: schedule.quantity,
          status: schedule.status,
          sourceType: "production_schedule" as const,
          // Enriched from Product
          productName: prod?.productName || "Unknown Product",
          productCode: prod?.productCode || "N/A",
          // Enriched from Form
          formName: frm?.formName || "Unknown Form",
          // Enriched from Department
          departmentId: deptId,
          departmentName: dept?.departmentName || "Unassigned",
          // Enriched from Sales Order Line
          lineNumber: orderLine?.lineNumber,
          linePriority: orderLine?.priority,
          anticipationDate: orderLine?.anticipationDate,
          lineStatus: orderLine?.status,
          // Enriched from Sales Order
          salesOrderId: orderLine?.salesOrderId,
          orderNumber: salesOrderData?.orderNumber,
          jobName: salesOrderData?.jobName,
          customerId: salesOrderData?.customerId,
          requiredDate: salesOrderData?.requiredDate,
        };
      });

    // Build enriched work orders
    const workOrderEntities: EnrichedScheduleEntity[] = (
      (workOrdersStore?.entities as workOrder[]) || []
    )
      .filter((wo) => wo.status !== "completed" && wo.status !== "rejected" && wo.scheduledDate)
      .map((wo) => {
        const prod = productsMap.get(wo.productId);
        const frm = formsMap.get(wo.formId || "");
        // Use work order's departmentId, or fall back to product's departmentId
        const deptId = wo.departmentId || prod?.departmentId || "";
        const dept = departmentsMap.get(deptId);

        return {
          id: wo.id,
          scheduleDate: wo.scheduledDate?.split("T")[0] || "",
          productId: wo.productId,
          formId: wo.formId || "",
          quantity: 1, // Work orders are per-piece
          status: wo.status,
          sourceType: "work_order" as const,
          // Enriched from Product
          productName: prod?.productName || "Unknown Product",
          productCode: prod?.productCode || "N/A",
          // Enriched from Form
          formName: frm?.formName || "Unassigned",
          // Enriched from Department
          departmentId: deptId,
          departmentName: dept?.departmentName || "Unassigned",
          // Work Order specific fields
          workOrderNumber: wo.workOrderNumber,
          pieceNumber: wo.pieceNumber,
          priority: wo.priority,
        };
      });

    // Merge both sources
    return [...productionEntities, ...workOrderEntities];
  }

  get dateRange(): string[] {
    // Get dates from query params, with defaults
    const today = new Date().toISOString().split("T")[0];
    const defaultEnd = new Date();
    defaultEnd.setDate(defaultEnd.getDate() + 90);
    const defaultEndDate = defaultEnd.toISOString().split("T")[0];

    const queryParams = this.rootStore.routerStore?.queryParams;
    const startDate = (queryParams?.startDate as string) || today;
    const endDate = (queryParams?.endDate as string) || defaultEndDate;

    return getDateRange(startDate, endDate, { weekdays: true });
  }

  get departmentProductionScheduleActive() {
    return this.rootStore.scheduleGridStore?.namespaces?.DepartmentProductionSchedule
      ? true
      : false;
  }

  // ========== Unscheduled Items Methods ==========

  // Get unscheduled count for a specific department
  getUnscheduledCountFor = (departmentId: string): number => {
    return this.getUnscheduledItemsFor(departmentId).length;
  };

  // Get unscheduled sales order lines for a department (with enriched product data)
  getUnscheduledItemsFor = (departmentId: string): (salesOrderLine & {
    productName: string;
    productCode: string;
    jobName?: string;
    orderNumber?: string;
  })[] => {
    const salesOrderLinesStore = this.rootStore.salesOrderLinesStore as ServiceStore;
    const productsStore = this.rootStore.productsStore as ServiceStore;
    const productionSchedulesStore = this.rootStore.productionSchedulesStore as ServiceStore;
    const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;

    if (!salesOrderLinesStore?.entities) return [];

    // Get IDs of lines that already have production schedules
    const scheduledLineIds = new Set(
      ((productionSchedulesStore?.entities as productionSchedule[]) || [])
        .map((ps) => ps.salesOrderLineId)
        .filter(Boolean)
    );

    // Build product lookup map
    const productsMap = new Map<string, product>(
      ((productsStore?.entities as product[]) || []).map((p) => [p.id, p])
    );

    // Build sales orders lookup map
    const salesOrdersMap = new Map<string, salesOrder>(
      ((salesOrdersStore?.entities as salesOrder[]) || []).map((so) => [so.id, so])
    );

    // Filter to pending lines without schedules, matching department
    return ((salesOrderLinesStore.entities as salesOrderLine[]) || [])
      .filter((line) => {
        // Must be pending status
        if (line.status !== "pending") return false;
        // Must not already have a production schedule
        if (scheduledLineIds.has(line.id)) return false;

        // Match by product's department
        const product = productsMap.get(line.productId);
        return product?.departmentId === departmentId;
      })
      .map((line) => {
        const product = productsMap.get(line.productId);
        const salesOrder = line.salesOrderId ? salesOrdersMap.get(line.salesOrderId) : undefined;

        return {
          ...line,
          productName: product?.productName || "Unknown Product",
          productCode: product?.productCode || "N/A",
          jobName: salesOrder?.jobName,
          orderNumber: salesOrder?.orderNumber,
        };
      })
      .sort((a, b) => {
        // Sort by priority DESC, then anticipationDate ASC
        if ((b.priority || 0) !== (a.priority || 0)) {
          return (b.priority || 0) - (a.priority || 0);
        }
        return (a.anticipationDate || "").localeCompare(b.anticipationDate || "");
      });
  };
}

export default DepartmentProductionScheduleStore;
